import { Injectable } from '@angular/core';
import { UserProfile } from '@osc-utils/models/user-profile';
import { filter, firstValueFrom, map, Observable, switchMap, take } from 'rxjs';
import { AccountInfo, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService, PlatformConfigService } from '@evc/platform';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class RtbAuthService {
  public claims$ = toObservable(this.authService.idTokenClaims);
  public hasAccessToApplication$: Observable<boolean> = toObservable(this.authService.idTokenClaims).pipe(
    filter(claims => !!claims),
    map(claims => !!(claims.organizationId && claims.licenseIds))
  );
  public isAuthenticated$ = toObservable(this.authService.isAuthenticated).pipe(filter(isAuthenticated => isAuthenticated !== undefined));
  private config = this.platformConfigService.config();
  private userProfile: UserProfile;

  public constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private platformConfigService: PlatformConfigService
  ) { }

  public get currentUserProfile(): UserProfile {
    return this.userProfile;
  }

  public login(request?: Partial<RedirectRequest>): void {
    firstValueFrom(this.authService.login(request));
  }

  public logout(): void {
    firstValueFrom(this.authService.logout());
  }

  public refreshTokens(): Observable<void> {
    const originalAccessToken = this.authService.accessToken();
    return this.authService.requestAccessToken().pipe(switchMap(result => result && this.authService.accessToken$.pipe(
      // HACK: We need to wait for the service to expose latest access token.
      // Otherwise, the old token might still be used in subsequent requests.
      filter(token => token && (token === result.accessToken || token !== originalAccessToken)),
      take(1),
      map(() => void 0)
    )));
  }

  public async isLoggedIn(): Promise<boolean> {
    return firstValueFrom(this.isAuthenticated$);
  }

  public async getCurrentUserProfile(): Promise<UserProfile> {
    const accessToken = await firstValueFrom(this.authService.accessToken$.pipe(filter(token => !!token)));
    const result = await firstValueFrom(this.httpClient.get<{ result: UserProfile }>(
      this.config.api.uri + this.config.api.endpoints.user.me, {
      headers: { Authorization: `Bearer ${accessToken}` }
    }));
    const claims = await firstValueFrom(this.claims$.pipe(filter(c => !!c)));
    result.result.role = claims.platformRoles as string;
    const userProfile = new UserProfile(result.result);
    // We have to set the user profile to ensure it follows the same pattern as the previous implementation from osc-utils.
    this.userProfile = userProfile;
    return userProfile;
  }

  public setUserProfile(userProfile: UserProfile): void {
    this.userProfile = userProfile;
  }

  public getIdTokenValue(): string {
    return this.authService.accessToken();
  }
}
